import { Fragment, useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, Send, Trash2, CheckCircle2, Clock, MailX, Loader2, ChevronDown, ChevronRight, Handshake, MessageCircle, FileDown, Loader, Pencil } from "lucide-react";
import ReferenceEditModal from "@/components/ReferenceEditModal";
import { useConfirm } from "@/components/ConfirmDialog";

const STATUS_META = {
  no_invite: { icon: MailX, cls: "border-neutral-900 text-[#F9F8F6] bg-neutral-800", key: "refStatusNoInvite" },
  pending: { icon: Clock, cls: "border-amber-700 text-neutral-900 bg-amber-300", key: "refStatusPending" },
  approved: { icon: CheckCircle2, cls: "border-emerald-800 text-white bg-emerald-600", key: "refStatusApproved" },
  declined: { icon: MailX, cls: "border-rose-800 text-white bg-rose-600", key: "refStatusDeclined" },
};

const EMPTY_FORM = { name: "", email: "", phone: "", relationship: "", language: "nl" };
const LANG_OPTS = [
  ["nl", "Nederlands"], ["en", "English"], ["de", "Deutsch"], ["fr", "Français"], ["es", "Español"],
];

const Info = ({ label, value }) => (
  <div>
    <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{label}</p>
    <p className="text-sm text-neutral-800 break-words">{value || "—"}</p>
  </div>
);

function PdfExportModal({ refs, onClose }) {
  const { t } = useT();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [language, setLanguage] = useState("nl");
  const [intro, setIntro] = useState(t("refsPdfIntroDefault"));
  const [selected, setSelected] = useState(new Set(refs.map((r) => r.id)));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cv-templates").then(({ data }) => setTemplates(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const generate = async () => {
    if (selected.size === 0) { toast.error(t("refsPdfPickAtLeastOne")); return; }
    setBusy(true);
    try {
      const res = await api.post(
        "/references/pdf",
        { template_id: templateId || null, reference_ids: Array.from(selected), intro_text: intro, language },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "referenties.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(t("refsPdfDownloaded"));
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("refsPdfFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[1px] flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="refs-pdf-modal"
        className="bg-[#F9F8F6] border border-neutral-900 shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <p className="ed-eyebrow mb-2">{t("refsPdfCta")}</p>
          <h3 className="font-serif text-2xl mb-4">{t("refsPdfTitle")}</h3>

          <p className="ed-label mb-1">{t("refsPdfTemplate")}</p>
          <select
            data-testid="refs-pdf-template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="ed-input mb-4"
          >
            <option value="">{t("refsPdfTemplateDefault")}</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name || "—"}</option>
            ))}
          </select>

          <p className="ed-label mb-1">{t("refsPdfLanguage")}</p>
          <select
            data-testid="refs-pdf-language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="ed-input mb-4"
          >
            <option value="nl">Nederlands</option>
            <option value="en">English</option>
          </select>

          <p className="ed-label mb-1">{t("refsPdfIntro")}</p>
          <textarea
            data-testid="refs-pdf-intro"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            rows={4}
            className="ed-input mb-4 font-serif"
          />

          <p className="ed-label mb-2">{t("refsPdfSelect")}</p>
          <div className="border border-neutral-300 max-h-52 overflow-y-auto divide-y divide-neutral-200 mb-5">
            {refs.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">{t("refsPdfNoApproved")}</p>
            ) : refs.map((r) => (
              <label key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`refs-pdf-pick-${r.id}`}
                  checked={selected.has(r.id)}
                  onChange={() => toggle(r.id)}
                  className="w-4 h-4 accent-neutral-900"
                />
                <div className="min-w-0">
                  <p className="font-serif text-base leading-tight truncate">{r.name}</p>
                  {(r.role_title || r.company) && (
                    <p className="text-xs text-neutral-500 italic truncate">
                      {[r.role_title, r.company].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button data-testid="refs-pdf-cancel" onClick={onClose} className="ed-btn ed-btn-outline">
              {t("cancel")}
            </button>
            <button data-testid="refs-pdf-generate" onClick={generate} disabled={busy} className="ed-btn">
              {busy ? <Loader size={13} strokeWidth={1.7} className="animate-spin" /> : <FileDown size={13} strokeWidth={1.7} />}
              {t("refsPdfGenerate")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function ReferencesPanel() {
  const confirm = useConfirm();
  const { t } = useT();
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [wa, setWa] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const shareViaWhatsapp = async () => {
    setWa(true);
    try {
      const { data } = await api.post("/references/quick-share");
      const msg = t("refsWaMessage", { url: data.url });
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
      await load();
      toast.success(t("refsWaOpened"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("refsWaFailed"));
    } finally {
      setWa(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/references");
      setRefs(data || []);
    } catch {
      toast.error(t("refsLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t("refsFieldsRequired"));
      return;
    }
    // Ask upfront whether to also send the invite email — prevents users from
    // adding a contact and forgetting to invite them (a common oversight).
    const sendNow = await confirm({
      title: t("refsInviteNowTitle"),
      body: t("refsInviteNowBody"),
      confirmLabel: t("refsInviteNowYes"),
      cancelLabel: t("refsInviteNowNo"),
      tone: "neutral",
    });
    setBusy(true);
    try {
      const { data } = await api.post("/references", form);
      setForm(EMPTY_FORM);
      if (sendNow && data?.id) {
        try {
          const inv = await api.post(`/references/${data.id}/invite`);
          if (inv.data.invite_status === "sent") toast.success(t("refsInviteSent"));
          else if (inv.data.invite_status === "skipped_no_key") toast.info(t("refsInviteNoKey"));
          else toast.error(t("refsInviteFailed"));
        } catch {
          toast.error(t("refsInviteFailed"));
        }
      } else {
        toast.success(t("refsAdded"));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("refsAddFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirm(t("refsConfirmDelete")))) return;
    try {
      await api.delete(`/references/${id}`);
      setRefs((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error(t("refsDeleteFailed"));
    }
  };

  const invite = async (id) => {
    setInvitingId(id);
    try {
      const { data } = await api.post(`/references/${id}/invite`);
      await load();
      if (data.invite_status === "sent") {
        toast.success(t("refsInviteSent"));
      } else if (data.invite_status === "skipped_no_key") {
        toast.info(t("refsInviteNoKey"));
      } else {
        toast.error(t("refsInviteFailed"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("refsInviteFailed"));
    } finally {
      setInvitingId(null);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/references/${id}`, { status });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("refsUpdateFailed"));
    }
  };

  const setLanguage = async (id, language) => {
    try {
      await api.patch(`/references/${id}`, { language });
      setRefs((prev) => prev.map((r) => (r.id === id ? { ...r, language } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("refsUpdateFailed"));
    }
  };

  return (
    <section data-testid="references-view">
      <p className="ed-eyebrow mb-2">{t("refsTitle")}</p>
      <h2 className="font-serif text-3xl mb-1">{t("refsHeading")}</h2>
      <p className="text-sm text-neutral-600 mb-6 max-w-2xl">{t("refsSubtitle")}</p>

      {/* Top actions: WhatsApp share + PDF export */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="refs-top-actions">
        <button
          data-testid="refs-wa-share"
          onClick={shareViaWhatsapp}
          disabled={wa}
          className="ed-btn ed-btn-outline"
          title={t("refsWaCta")}
        >
          {wa
            ? <Loader2 size={13} strokeWidth={1.7} className="animate-spin" />
            : <MessageCircle size={13} strokeWidth={1.7} />}
          {t("refsWaCta")}
        </button>
        <button
          data-testid="refs-pdf-open"
          onClick={() => setPdfOpen(true)}
          disabled={refs.filter((r) => r.status === "approved").length === 0}
          className="ed-btn ed-btn-outline"
          title={t("refsPdfCta")}
        >
          <FileDown size={13} strokeWidth={1.7} /> {t("refsPdfCta")}
        </button>
      </div>

      {pdfOpen && (
        <PdfExportModal
          refs={refs.filter((r) => r.status === "approved")}
          onClose={() => setPdfOpen(false)}
        />
      )}

      {/* Add form */}
      <div className="ed-card p-5 mb-8" data-testid="refs-add-form">
        <p className="ed-label mb-3">{t("refsAddTitle")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="refs-input-name" className="ed-input" placeholder={t("refsFieldName")}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="refs-input-email" type="email" className="ed-input" placeholder={t("refsFieldEmail")}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="refs-input-phone" className="ed-input" placeholder={t("refsFieldPhone")}
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input data-testid="refs-input-relationship" className="ed-input" placeholder={t("refsFieldRelationship")}
            value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} />
          <select
            data-testid="refs-input-language"
            className="ed-input"
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            title={t("refsFieldLanguage")}
          >
            {LANG_OPTS.map(([code, label]) => (
              <option key={code} value={code}>{t("refsFieldLanguage")}: {label}</option>
            ))}
          </select>
        </div>
        <div className="mt-4">
          <button data-testid="refs-add-btn" onClick={add} disabled={busy} className="ed-btn">
            <Plus size={14} strokeWidth={1.5} /> {t("refsAddCta")}
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="font-mono text-xs text-neutral-500">{t("loading")}</p>
      ) : refs.length === 0 ? (
        <div className="ed-card p-10 text-center" data-testid="refs-empty">
          <p className="font-serif text-lg text-neutral-500">{t("refsEmpty")}</p>
        </div>
      ) : (
        <div className="ed-card overflow-x-auto" data-testid="refs-table">
          <table className="ed-table">
            <thead>
              <tr>
                <th>{t("refsColName")}</th>
                <th>{t("refsColContact")}</th>
                <th>{t("refsColRelationship")}</th>
                <th>{t("refsColLanguage")}</th>
                <th>{t("refsColStatus")}</th>
                <th className="text-right">{t("refsColActions")}</th>
              </tr>
            </thead>
            <tbody>
              {refs.map((r, idx) => {
                const meta = STATUS_META[r.status] || STATUS_META.no_invite;
                const Icon = meta.icon;
                const isOpen = expandedId === r.id;
                return (
                  // React needs a Fragment WITH a key when a map()
                  // returns multiple sibling <tr> elements — the
                  // shorthand `<>` cannot carry a key, and its absence
                  // caused a "unique key" warning that could escalate
                  // into a full render bail-out (blank references
                  // screen after saving) under Concurrent-Mode.
                  <Fragment key={r.id}>
                  <tr
                    data-testid={`refs-row-${idx}`}
                    onClick={() => toggleExpand(r.id)}
                    className="cursor-pointer hover:bg-neutral-50 transition-colors"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpand(r.id); } }}
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        {isOpen ? <ChevronDown size={12} strokeWidth={1.7} className="text-neutral-500" /> : <ChevronRight size={12} strokeWidth={1.7} className="text-neutral-500" />}
                        <div>
                          <p className="font-serif text-base">{r.name}</p>
                          {r.role_title || r.company ? (
                            <p className="text-xs text-neutral-500 italic mt-0.5">
                              {[r.role_title, r.company].filter(Boolean).join(" · ")}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-neutral-700">
                      <p>{r.email}</p>
                      {r.phone ? <p className="text-xs text-neutral-500">{r.phone}</p> : null}
                    </td>
                    <td className="text-sm text-neutral-700 italic">{r.relationship || "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        data-testid={`refs-language-${idx}`}
                        value={r.language || "nl"}
                        onChange={(e) => setLanguage(r.id, e.target.value)}
                        className="ed-input text-xs font-mono uppercase tracking-[0.1em] border py-1 px-2"
                        style={{ maxWidth: "6rem" }}
                        title={t("refsFieldLanguage")}
                      >
                        {LANG_OPTS.map(([code, _label]) => (
                          <option key={code} value={code}>{code.toUpperCase()}</option>
                        ))}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.status === "declined" ? (
                        <span
                          data-testid={`refs-status-${idx}`}
                          className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] ${STATUS_META.declined.cls}`}
                        >
                          <Icon size={10} strokeWidth={1.7} />
                          {t("refStatusDeclined")}
                        </span>
                      ) : (
                        <div
                          data-testid={`refs-status-${idx}`}
                          role="radiogroup"
                          aria-label={t("refsColStatus")}
                          className="inline-flex items-stretch border border-neutral-300 divide-x divide-neutral-300"
                        >
                          {[
                            { value: "no_invite", key: "refStatusNoInvite", meta: STATUS_META.no_invite },
                            { value: "pending", key: "refStatusPending", meta: STATUS_META.pending },
                            { value: "approved", key: "refStatusApproved", meta: STATUS_META.approved },
                          ].map(({ value, key, meta: m }) => {
                            const active = r.status === value;
                            const MetaIcon = m.icon;
                            return (
                              <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                data-testid={`refs-status-${idx}-${value}`}
                                onClick={() => setStatus(r.id, value)}
                                title={t(key)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${
                                  active
                                    ? m.cls
                                    : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"
                                }`}
                              >
                                <MetaIcon size={10} strokeWidth={1.7} />
                                <span className="hidden sm:inline">{t(key)}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`refs-edit-${idx}`}
                          onClick={() => setEditingId(r.id)}
                          title={t("refEditCta")}
                          className="p-1.5 border border-neutral-300 hover:border-neutral-900 transition-colors"
                        >
                          <Pencil size={13} strokeWidth={1.7} />
                        </button>
                        <button
                          data-testid={`refs-invite-${idx}`}
                          onClick={() => invite(r.id)}
                          disabled={invitingId === r.id}
                          title={r.status === "no_invite" ? t("refsInviteFlashHint") : t("refsInviteCta")}
                          className={`p-1.5 border border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-50 ${r.status === "no_invite" && invitingId !== r.id ? "ed-flash" : ""}`}
                        >
                          {invitingId === r.id
                            ? <Loader2 size={13} strokeWidth={1.7} className="animate-spin" />
                            : <Send size={13} strokeWidth={1.7} />}
                        </button>
                        <button
                          data-testid={`refs-delete-${idx}`}
                          onClick={() => remove(r.id)}
                          title={t("delete")}
                          className="p-1.5 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors"
                        >
                          <Trash2 size={13} strokeWidth={1.7} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`refs-detail-${idx}`} className="bg-[#FBFAF7]">
                      <td colSpan={6} className="!py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                          <Info label={t("refsFieldName")} value={r.name} />
                          <Info label={t("refsFieldEmail")} value={r.email} />
                          <Info label={t("refsFieldPhone")} value={r.phone} />
                          <Info label={t("refsFieldRelationship")} value={r.relationship} />
                          <Info label="Rol / functie" value={r.role_title} />
                          <Info label="Bedrijf" value={r.company} />
                          <Info label="Jaren bekend" value={r.years_known} />
                          <Info label={t("refsColLanguage")} value={(r.language || "nl").toUpperCase()} />
                          <Info label={t("refsColStatus")} value={t(STATUS_META[r.status]?.key || "refStatusNoInvite")} />
                          <Info label="Uitgenodigd op" value={r.invited_at ? new Date(r.invited_at).toLocaleString() : "—"} />
                          <Info label="Goedgekeurd op" value={r.approved_at ? new Date(r.approved_at).toLocaleString() : "—"} />
                          {r.note && <div className="sm:col-span-2 md:col-span-3">
                            <p className="ed-label">Notitie</p>
                            <p className="text-sm text-neutral-700 whitespace-pre-wrap italic">{r.note}</p>
                          </div>}
                          {(r.share_fields && r.share_fields.length > 0) && (
                            <div className="sm:col-span-2 md:col-span-3">
                              <p className="ed-label mb-1">Gedeelde gegevens</p>
                              <div className="flex flex-wrap gap-1.5">
                                {r.share_fields.map((f) => (
                                  <span key={f} className="font-mono text-[0.6rem] uppercase tracking-[0.12em] border border-neutral-300 bg-white px-2 py-0.5">{f}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-neutral-500 mt-5 max-w-2xl">
        {t("refsPublicNote")}
      </p>

      {editingId && (
        <ReferenceEditModal
          referenceId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={(updated) => {
            setRefs((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
          }}
        />
      )}
    </section>
  );
}
